import React, { useState, useLayoutEffect } from 'react';
import { Controller, Control, FormState, UseFormSetValue, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Alert, Divider, IconButton, InputAdornment, TextField } from '@mui/material';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ErrorOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { SignInFormValues } from '../model/types';
import { useStyles } from './styles';
import { UnauthenticatedPath } from '@shared/constants/routes.constants';
import { emailRules, signInPasswordRules } from '@shared/constants/validation.constants';

interface SignInFormProps {
  title?: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  control: Control<SignInFormValues>;
  setValue: UseFormSetValue<SignInFormValues>;
  errorMsg: string;
  formState: FormState<SignInFormValues>;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  title = 'Sign in',
  onSubmit,
  control,
  setValue,
  errorMsg,
  formState,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);

  const loginValue = useWatch({ control, name: 'login' });

  useLayoutEffect(() => {
    const trimLoginValue = loginValue.trim();
    setValue('login', trimLoginValue);
  }, [loginValue, setValue]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // A full navigation, not an XHR: the API answers with a redirect to the
  // identity provider on another origin, and the browser has to follow it and
  // render its login form.
  const signInWithSso = () => {
    window.location.assign(`${import.meta.env.VITE_API_URL}/v1/auth/oidc/authorize`);
  };

  return (
    <Box className={classes.root} component="form" onSubmit={onSubmit}>
      <Box className={classes.header}>
        <Typography className={classes.title} variant="h3" component="div">
          {title}
        </Typography>
        <Typography className={classes.description} component="div">
          Please enter your email and password
        </Typography>
        {errorMsg && (
          <Alert
            severity="error"
            className={classes.errorAlert}
            icon={<ErrorOutline className={classes.errorIcon} />}
            role="alert"
            aria-live="polite">
            {errorMsg}
          </Alert>
        )}
      </Box>
      <Box className={classes.main}>
        <Box className={classes.field}>
          <Typography className={classes.fieldLabel} component="label" htmlFor="sign-in-email">
            Email
          </Typography>
          <Controller
            name="login"
            control={control}
            defaultValue=""
            rules={emailRules}
            render={({ field: { ref, ...fieldProps } }) => (
              <TextField
                className={classes.fieldInput}
                size="small"
                color="info"
                id="sign-in-email"
                placeholder="Enter your email"
                error={!!formState.errors.login}
                helperText={formState.errors.login ? formState.errors.login.message : ' '}
                {...fieldProps}
              />
            )}
          />
        </Box>
        <Box className={classes.field}>
          <Typography className={classes.fieldLabel} component="label" htmlFor="sign-in-password">
            Password
          </Typography>
          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={signInPasswordRules}
            render={({ field }) => (
              <TextField
                className={classes.fieldInput}
                id="sign-in-password"
                placeholder="Enter your password"
                type={showPassword ? 'text' : 'password'}
                size="small"
                error={!!formState.errors.password}
                helperText={formState.errors.password ? formState.errors.password.message : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...field}
              />
            )}
          />
        </Box>
        <Box className={classes.login_btn}>
          <Button fullWidth variant="contained" type="submit">
            Sign in
          </Button>
        </Box>
        <Link className={classes.forgotPasswordLink} to={UnauthenticatedPath.FORGOT}>
          {t('formValidation.labels.forgotPassword')}
        </Link>
        <Divider className={classes.ssoDivider}>or</Divider>
        <Button
          className={classes.ssoButton}
          variant="outlined"
          type="button"
          onClick={signInWithSso}>
          Sign in with SSO
        </Button>
      </Box>
    </Box>
  );
};
